import { supabase } from '@/utils/supabase'
import { getCache, setCache, hasCache, cacheStore } from '@/utils/cache'
import { PLATFORM_TABLE_MAP } from '@/utils/security'

const CACHE_TTL = 60000

function getTables(platform) {
  return PLATFORM_TABLE_MAP[platform] || PLATFORM_TABLE_MAP.mobile_delta
}

export async function getOrdersByStatus(handlerId, status, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const cacheKey = `orders_${platform}_${handlerId}_${status}`
  
  if (hasCache(cacheKey)) {
    return getCache(cacheKey)
  }
  
  const { data, error } = await supabase
    .from(tables.orders)
    .select('*')
    .eq('handler_id', handlerId)
    .eq('status', status)
    .order('created_at', { ascending: false })
  
  if (!error && data) {
    setCache(cacheKey, data, CACHE_TTL)
  }
  
  return { data, error }
}

export async function getAllCompletedOrders(handlerId, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const cacheKey = `completed_orders_${platform}_${handlerId}`
  
  if (hasCache(cacheKey)) {
    return getCache(cacheKey)
  }
  
  const { data, error } = await supabase
      .from(tables.orders)
      .select('*')
      .eq('handler_id', handlerId)
      .in('status', ['completed', 'finished'])
      .order('finished_at', { ascending: false })
  
  if (!error && data) {
    setCache(cacheKey, data, CACHE_TTL)
  }
  
  return { data, error }
}

export async function getGoodsInfo(goodsId) {
  const cacheKey = `goods_${goodsId}`
  
  if (hasCache(cacheKey)) {
    return getCache(cacheKey)
  }
  
  const { data, error } = await supabase
    .from('goods')
    .select('desc')
    .eq('id', goodsId)
    .limit(1)
  
  if (!error && data) {
    setCache(cacheKey, data, CACHE_TTL * 5)
  }
  
  return { data, error }
}

export async function updateOrderStatus(orderId, status, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
      .from(tables.orders)
      .update({
        status,
        finished_at: (status === 'completed' || status === 'finished' || status === 'pending_review') ? new Date().toISOString() : null
      })
      .eq('id', orderId)
  
  if (!error) {
    clearOrderCache()
  }
  
  return { data, error }
}

export async function getTransferOrders(handlerId, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.transfer_orders)
    .select('*')
    .eq('transfer_from', handlerId)
    .order('transfer_time', { ascending: false })
  
  if (error && error.code === 'PGRST116') {
    return { data: [], error: null }
  }
  
  return { data, error }
}

export async function getFines(player, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.fines)
    .select('*')
    .eq('player', player)
    .order('created_at', { ascending: false })
  
  if (error && error.code === 'PGRST116') {
    return { data: [], error: null }
  }
  
  return { data, error }
}

export async function getDeposit(player, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.deposits)
    .select('player, current')
    .eq('player', player)
    .limit(1)
  
  if (error && error.code === 'PGRST116') {
    return { data: [], error: null }
  }
  
  return { data, error }
}

export async function createFine(player, amount, reason, orderId, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.fines)
    .insert({
      player,
      amount,
      reason,
      order_id: orderId,
      paid: false,
      created_at: new Date().toISOString()
    })
    .select()
  
  return { data, error }
}

export async function payFine(fineId, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.fines)
    .update({ paid: true })
    .eq('id', fineId)
  
  return { data, error }
}

export async function createTransferOrder(orderId, transferFrom, transferTime, transferReason, originalPrice, newPrice, fineAmount, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.transfer_orders)
    .insert({
      order_id: orderId,
      transfer_from: transferFrom,
      transfer_time: transferTime,
      transfer_reason: transferReason,
      original_price: originalPrice,
      new_price: newPrice,
      fine_amount: fineAmount
    })
    .select()
  return { data, error }
}

export function clearOrderCache() {
  for (const key of cacheStore.keys()) {
    if (key.startsWith('orders_') || key.startsWith('completed_orders_')) {
      cacheStore.delete(key)
    }
  }
}

export async function getXianyuOrdersByStatus(player, status, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .select('*')
    .eq('player', player)
    .eq('status', status)
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function getAllXianyuCompletedOrders(player, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .select('*')
    .eq('player', player)
    .in('status', ['finished', 'completed'])
    .order('fish_time', { ascending: false })
  
  return { data, error }
}

export async function updateXianyuOrderStatus(orderId, status, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .update({
      status,
      fish_time: (status === 'finished' || status === 'pending_review') ? new Date().toISOString() : null
    })
    .eq('id', orderId)
  
  return { data, error }
}

export async function getPendingOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.orders)
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function acceptOrder(orderId, handlerId, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.orders)
    .update({
      status: 'processing',
      handler_id: handlerId
    })
    .eq('id', orderId)
    .select()
  
  if (!error) {
    clearOrderCache()
  }
  
  return { data, error }
}

export async function getProcessingOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.orders)
    .select('*')
    .eq('status', 'processing')
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function getPendingReviewOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.orders)
    .select('*')
    .eq('status', 'pending_review')
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function getAllOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.orders)
    .select('*')
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function getXianyuPendingOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function acceptXianyuOrder(orderId, player, platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .update({
      status: 'processing',
      player
    })
    .eq('id', orderId)
    .select()
  
  return { data, error }
}

export async function getXianyuProcessingOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .select('*')
    .eq('status', 'processing')
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function getXianyuPendingReviewOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .select('*')
    .eq('status', 'pending_review')
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export async function getAllXianyuOrders(platform = 'mobile_delta') {
  const tables = getTables(platform)
  const { data, error } = await supabase
    .from(tables.xianyu_order)
    .select('*')
    .order('created_at', { ascending: false })
  
  return { data, error }
}